import { type Common } from '../../common.js';
import { type FileReference } from '../../models/flow/file.js';
import {
  type DocumentTranscriptionResponses,
  type FhirLabValues,
} from '../../models/flow/humanTask/submitPriorAuthorization.js';

export class API {
  common: Common;

  constructor(common: Common) {
    this.common = common;
  }

  getBaseEndpoint(orderId: string) {
    return `/patient-orders/clinicals/${orderId}/packets`;
  }

  async create(args: {
    orderId: string;
    sourceDocuments: NonNullable<DocumentTranscriptionResponses>;
    labValues?: FhirLabValues;
    relevantDocumentIds?: string[];
  }): Promise<FileReference> {
    const { orderId, sourceDocuments, labValues, relevantDocumentIds } = args;
    const url = this.common.constructPath(this.getBaseEndpoint(orderId));
    return await this.common.postJSON(url, {
      sourceDocuments,
      labValues,
      relevantDocumentIds,
    });
  }

  async previewDocument(args: {
    orderId: string;
    sourceDocument: NonNullable<DocumentTranscriptionResponses>[0];
  }): Promise<{ status: number; dataURL: string }> {
    const { orderId, sourceDocument } = args;

    const url = this.common.constructPath(`${this.getBaseEndpoint(orderId)}/preview-document`);

    const response = await this.common.authedFetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/pdf',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sourceDocument }),
    });

    const { status } = response;
    const blob = await response.blob();
    return { status, dataURL: URL.createObjectURL(blob) };
  }

  async formatDocument(args: {
    orderId: string;
    sourceDocument: NonNullable<DocumentTranscriptionResponses>[0];
  }): Promise<{ sourceDocument: NonNullable<DocumentTranscriptionResponses>[0] }> {
    const { orderId, sourceDocument } = args;

    const url = this.common.constructPath(`${this.getBaseEndpoint(orderId)}/format-document`);

    return await this.common.postJSON(url, {
      sourceDocument,
    });
  }

  async summarizeDocument(args: {
    orderId: string;
    sourceDocument: NonNullable<DocumentTranscriptionResponses>[0];
  }): Promise<{ documentId: string; title: string }> {
    const { orderId, sourceDocument } = args;

    const url = this.common.constructPath(`${this.getBaseEndpoint(orderId)}/summarize-document`);
    const response = await this.common.postJSON(url, {
      sourceDocument,
    });

    return { documentId: sourceDocument.documentId, ...response };
  }
}
